import { Box, Skeleton } from '@chakra-ui/react';
import { useCachedImage } from '../hooks/useCachedImage';

interface CachedImageProps {
  src: string;
  alt: string;
  fallbackType?: string;
  size?: string;
  width?: string | number;
  height?: string | number;
  objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
  borderRadius?: string;
  [key: string]: any; // Allow other props to be passed through
}

const CachedImage: React.FC<CachedImageProps> = ({
  src,
  alt,
  fallbackType = 'default',
  size = 'w500',
  width,
  height,
  objectFit = 'cover',
  borderRadius,
  ...props
}) => {
  const { imageUrl, isLoading } = useCachedImage(src, {
    fallbackType,
    size
  });

  if (isLoading) {
    return (
      <Skeleton
        width={width}
        height={height}
        borderRadius={borderRadius}
        {...props}
      />
    );
  }

  return (
    <Box
      position="relative"
      width={width}
      height={height}
      borderRadius={borderRadius}
      overflow="hidden"
      bg="gray.100"
      _dark={{ bg: "gray.700" }}
      {...props}
    >
      <Box
        as="img"
        src={imageUrl}
        alt={alt}
        width="100%"
        height="100%"
        objectFit={objectFit}
        borderRadius={borderRadius}
        onError={(e) => {
          // Fallback to local SVG placeholder if image fails to load
          const target = e.target as HTMLImageElement;
          const fallbackSvg = `data:image/svg+xml;base64,${btoa(`
            <svg width="${width || 342}" height="${height || 513}" viewBox="0 0 ${width || 342} ${height || 513}" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="${width || 342}" height="${height || 513}" fill="#1A202C"/>
              <text x="50%" y="50%" font-family="Arial, sans-serif" font-size="24" fill="#718096" text-anchor="middle" dominant-baseline="middle">${fallbackType}</text>
            </svg>
          `)}`;
          target.src = fallbackSvg;
        }}
        transition="transform 0.2s ease-in-out"
        _hover={{ transform: "scale(1.05)" }}
      />
    </Box>
  );
};

export default CachedImage;
